// src/config/index.js
//
// Single source of truth for every external origin the app talks to.
//
// Nothing in src/ should ever hardcode a host again — import from here instead.
// Switching between local development and production is purely a matter of the
// environment files (.env.development / .env.production); no source change.
//
//   REACT_APP_API_URL          main backend API  (localhost:3000 / checking.shorthandonlineexam.in)
//   REACT_APP_COMPARE_API_URL  passage-compare service (localhost:5002 / api.shorthandonlineexam.in)
//   REACT_APP_FRONTEND_URL     this app's own public origin
//
// NOTE: Create React App inlines `process.env.REACT_APP_*` at build time, so the
// full expression must appear literally below — destructuring `process.env` or
// building the key dynamically will not work.

// Fallbacks used when an environment file is missing, so a fresh clone still
// runs against a local backend without any setup.
const DEFAULT_API_URL = 'http://localhost:3000';
const DEFAULT_COMPARE_API_URL = 'http://localhost:5002';

// Keeps `${API_BASE_URL}/path` from producing a double slash when someone sets
// the variable with a trailing slash.
const stripTrailingSlash = (value) => value.replace(/\/+$/, '');

const resolveUrl = (value, fallback) => stripTrailingSlash((value || '').trim() || fallback);

/** Main backend API. Used by virtually every axios call in the app. */
export const API_BASE_URL = resolveUrl(process.env.REACT_APP_API_URL, DEFAULT_API_URL);

/** Passage text-comparison service (the Python `/compare` API). */
export const COMPARE_API_BASE_URL = resolveUrl(
    process.env.REACT_APP_COMPARE_API_URL,
    DEFAULT_COMPARE_API_URL
);

/**
 * This app's own public origin. Only needed when an absolute self-referencing
 * link has to be produced; falls back to the origin the app is actually served
 * from, which is correct in both dev and production.
 */
export const FRONTEND_URL = resolveUrl(
    process.env.REACT_APP_FRONTEND_URL,
    typeof window !== 'undefined' ? window.location.origin : ''
);

/** Joins a path onto the backend API base, tolerating a missing leading slash. */
export const buildApiUrl = (path = '') =>
    `${API_BASE_URL}${path && !path.startsWith('/') ? '/' : ''}${path}`;

/** Joins a path onto the compare service base. */
export const buildCompareUrl = (path = '') =>
    `${COMPARE_API_BASE_URL}${path && !path.startsWith('/') ? '/' : ''}${path}`;

const config = {
    API_BASE_URL,
    COMPARE_API_BASE_URL,
    FRONTEND_URL,
    buildApiUrl,
    buildCompareUrl,
};

export default config;
